/**
 * Visualizador de transcrição HÍBRIDO
 *
 * Modo Inline: Paginação simples (sem conflitos de CSS com WordPress)
 * Modo Fullscreen: Dialog com virtualização via react-virtuoso (performance máxima)
 *
 * Esta abordagem resolve o conflito entre:
 * - WordPress/MUI Tabs que precisam de overflow: visible
 * - Listas virtualizadas que precisam de overflow: hidden
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Collapse,
  Badge,
  Slider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
  Pagination,
  AppBar,
  Toolbar,
  Fade,
  Snackbar,
  Alert
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PersonIcon from '@mui/icons-material/Person'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import HighlightIcon from '@mui/icons-material/Highlight'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import EditNoteIcon from '@mui/icons-material/EditNote'
import FilterListIcon from '@mui/icons-material/FilterList'
import TuneIcon from '@mui/icons-material/Tune'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import HeadphonesIcon from '@mui/icons-material/Headphones'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { Transcription, TranscriptSegment, UserAnnotations, Annotation, SegmentHighlight, Bookmark, AnnotationColor, AnnotationCategory } from '../../../types/transcription'
import { searchTranscript, formatTimestamp, highlightLegalTerms } from '../../../services/transcriptionAnalysis'
import { CATEGORY_COLORS } from '../../../constants/termColors'
import { SpeakerMapping } from '../../../services/exportService'
import SpeakerEditor from './SpeakerEditor'
import TranscriptSegmentItem, { ANNOTATION_COLORS, ANNOTATION_CATEGORIES, SegmentAnnotations } from './TranscriptSegmentItem'
import { saveSegmentEdits } from '../../../services/api'
import { useMergedCustomTerms } from '../../../hooks/useCustomTerms'
import ToolsSidePanel from './ToolsSidePanel'
import ToolsLegendBar from './ToolsLegendBar'
import LabelIcon from '@mui/icons-material/Label'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import CustomTermsManager, { CustomTermsManagerHandle } from './CustomTermsManager'
import AnnotationsPanel, { AnnotationsPanelHandle } from './AnnotationsPanel'
import AIChatPanel from './AIChatPanel'

// Tipos para filtros
type AnnotationFilterType = 'all' | 'bookmarks' | 'notes' | 'highlights' | 'any-annotation'
type ConfidenceFilterType = 'all' | 'low' | 'medium' | 'high'

// Constantes
const ITEMS_PER_PAGE = 25 // Itens por página no modo inline

type Props = {
  transcription: Transcription
  onTimestampClick?: (seconds: number) => void
  highlightTerms?: boolean
  speakerMapping?: SpeakerMapping
  onSpeakerMappingChange?: (mapping: SpeakerMapping) => void
  scrollToSegmentIndex?: number | null
  activeSegmentIndex?: number | null
  annotations?: UserAnnotations
  onAnnotationsChange?: (annotations: UserAnnotations) => void
  /** Se o player de mídia está habilitado/visível */
  playerEnabled?: boolean
  /** Callback para habilitar/desabilitar o player */
  onTogglePlayer?: () => void
  /** Se a mídia está disponível no backend */
  mediaAvailable?: boolean
  /** ID do arquivo para edição de segmentos */
  fileId?: string
  /** Callback quando a transcrição é atualizada (após save de edições) */
  onTranscriptionChange?: (updatedTranscription: Transcription) => void
  /** Callback para pausar áudio ao iniciar edição/nota */
  onPauseForAction?: () => void
  /** Callback para retomar áudio após concluir/cancelar edição/nota */
  onResumeAfterAction?: () => void
  /** Callback para reproduzir apenas o trecho de um segmento */
  onPlaySegment?: (startTime: number, endTime: number) => void
  /** Índice do segmento sendo reproduzido em modo segmento (null se nenhum) */
  playingSegmentIndex?: number | null
  /** Callback para parar reprodução de segmento */
  onStopSegmentPlayback?: () => void
}

export default function TranscriptViewerHybrid({
  transcription,
  onTimestampClick,
  highlightTerms = true,
  speakerMapping = {},
  onSpeakerMappingChange,
  scrollToSegmentIndex,
  activeSegmentIndex,
  annotations,
  onAnnotationsChange,
  playerEnabled = false,
  onTogglePlayer,
  mediaAvailable = false,
  fileId,
  onTranscriptionChange,
  onPauseForAction,
  onResumeAfterAction,
  onPlaySegment,
  playingSegmentIndex = null,
  onStopSegmentPlayback
}: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Busca termos customizados do usuário (GLOBAL + FILE)
  const { terms: customTerms } = useMergedCustomTerms(undefined, fileId)

  // Painel lateral de ferramentas
  const [activeTool, setActiveTool] = useState<string | null>(null)
  const [highlightTermsVisible, setHighlightTermsVisible] = useState(true)
  const [annotationsVisible, setAnnotationsVisible] = useState(true)
  const termsManagerRef = useRef<CustomTermsManagerHandle>(null)
  const annotationsPanelRef = useRef<AnnotationsPanelHandle>(null)

  const toolsConfig = useMemo(() => [
    { id: 'terms', icon: <LabelIcon fontSize="small" />, label: 'Termos Customizados', tooltip: 'Gerenciar termos destacados' },
    { id: 'annotations', icon: <EditNoteIcon fontSize="small" />, label: 'Anotações', tooltip: 'Gerenciar notas, destaques e favoritos' },
    { id: 'chat', icon: <ChatBubbleOutlineIcon fontSize="small" />, label: 'Chat IA', tooltip: 'Conversar com IA sobre a transcrição' },
  ], [])

  // Estado principal
  const [searchQuery, setSearchQuery] = useState('')
  const [currentResultIndex, setCurrentResultIndex] = useState(0)
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [copySuccess, setCopySuccess] = useState(false)
  const [copySnackbar, setCopySnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  })

  // Refs
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  // Ref para sempre ter o annotations mais recente (evita stale closure em cliques rápidos)
  const annotationsRef = useRef(annotations)
  annotationsRef.current = annotations

  // Estados para menu de contexto e diálogos de anotação
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; segmentIndex: number } | null>(null)
  const [annotationDialog, setAnnotationDialog] = useState<{ open: boolean; segmentIndex: number; editingId?: string } | null>(null)
  const [annotationText, setAnnotationText] = useState('')
  const [annotationColor, setAnnotationColor] = useState<AnnotationColor>('yellow')
  const [annotationCategory, setAnnotationCategory] = useState<AnnotationCategory>('note')

  // Estados de filtros avançados
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [annotationFilter, setAnnotationFilter] = useState<AnnotationFilterType>('all')
  const [selectedSpeakers, setSelectedSpeakers] = useState<string[]>([])
  const [confidenceFilter, setConfidenceFilter] = useState<ConfidenceFilterType>('all')
  const [highlightColorFilter, setHighlightColorFilter] = useState<AnnotationColor | 'all'>('all')

  // Estado para highlight de segmento navegado externamente
  const [highlightedSegment, setHighlightedSegment] = useState<number | null>(null)

  // Calcular duração total da transcrição em segundos
  const totalDuration = useMemo(() => {
    if (transcription.transcript.length === 0) return 0
    const lastSegment = transcription.transcript[transcription.transcript.length - 1]
    return parseFloat(lastSegment.end_time) || parseFloat(lastSegment.start_time) + 10
  }, [transcription])

  // Estado do slider de tempo [min, max] em segundos
  const [timeRange, setTimeRange] = useState<[number, number]>([0, totalDuration])
  const [isTimeFilterActive, setIsTimeFilterActive] = useState(false)

  // Atualizar timeRange quando totalDuration muda
  useEffect(() => {
    if (!isTimeFilterActive) {
      setTimeRange([0, totalDuration])
    }
  }, [totalDuration, isTimeFilterActive])

  // Formatar segundos para exibição
  const formatSeconds = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }, [])

  // Função para obter o nome do speaker (mapeado ou original)
  const getSpeakerName = useCallback((speaker: string) => {
    return speakerMapping[speaker] || speaker
  }, [speakerMapping])

  const searchResults = useMemo(() => {
    return searchTranscript(transcription, searchQuery)
  }, [transcription, searchQuery])

  const resultSegmentIndices = useMemo(() => {
    return [...new Set(searchResults.map(r => r.segmentIndex))]
  }, [searchResults])

  // Helpers para verificar anotações de um segmento (por índice)
  const getSegmentAnnotationsByIndex = useCallback((segmentIndex: number): SegmentAnnotations => {
    if (!annotations) return { notes: [], highlights: [], bookmarks: [] }
    return {
      notes: annotations.items.filter(a => a.segmentIndex === segmentIndex),
      highlights: annotations.highlights.filter(h => h.segmentIndex === segmentIndex),
      bookmarks: annotations.bookmarks.filter(b => b.segmentIndex === segmentIndex)
    }
  }, [annotations])

  // Verificar se algum filtro avançado está ativo
  const hasActiveFilters = useMemo(() => {
    return isTimeFilterActive ||
           annotationFilter !== 'all' ||
           selectedSpeakers.length > 0 ||
           confidenceFilter !== 'all' ||
           highlightColorFilter !== 'all' ||
           searchQuery.trim() !== ''
  }, [isTimeFilterActive, annotationFilter, selectedSpeakers, confidenceFilter, highlightColorFilter, searchQuery])

  // Contar filtros ativos para o badge
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (isTimeFilterActive) count++
    if (annotationFilter !== 'all') count++
    if (selectedSpeakers.length > 0) count++
    if (confidenceFilter !== 'all') count++
    if (highlightColorFilter !== 'all') count++
    return count
  }, [isTimeFilterActive, annotationFilter, selectedSpeakers, confidenceFilter, highlightColorFilter])

  // Filtrar segmentos com todos os critérios - retorna {segment, originalIndex}[]
  const filteredTranscript = useMemo(() => {
    const result: Array<{ segment: TranscriptSegment; originalIndex: number }> = []

    transcription.transcript.forEach((segment, index) => {
      // Filtro de tempo
      if (isTimeFilterActive) {
        const startTime = parseFloat(segment.start_time)
        if (startTime < timeRange[0] || startTime > timeRange[1]) return
      }

      // Filtro de speaker
      if (selectedSpeakers.length > 0 && segment.speaker) {
        if (!selectedSpeakers.includes(segment.speaker)) return
      }

      // Filtro de confiança
      if (confidenceFilter !== 'all') {
        const confidence = segment.confidence || 1
        if (confidenceFilter === 'low' && confidence >= 0.7) return
        if (confidenceFilter === 'medium' && (confidence < 0.7 || confidence >= 0.9)) return
        if (confidenceFilter === 'high' && confidence < 0.9) return
      }

      // Filtro de anotações
      if (annotationFilter !== 'all' && annotations) {
        const segAnn = getSegmentAnnotationsByIndex(index)
        const hasBookmarks = segAnn.bookmarks.length > 0
        const hasNotes = segAnn.notes.length > 0
        const hasHighlights = segAnn.highlights.length > 0

        if (annotationFilter === 'bookmarks' && !hasBookmarks) return
        if (annotationFilter === 'notes' && !hasNotes) return
        if (annotationFilter === 'highlights' && !hasHighlights) return
        if (annotationFilter === 'any-annotation' && !hasBookmarks && !hasNotes && !hasHighlights) return
      }

      // Filtro de cor de destaque
      if (highlightColorFilter !== 'all' && annotations) {
        const segAnn = getSegmentAnnotationsByIndex(index)
        const hasColorHighlight = segAnn.highlights.some(h => h.color === highlightColorFilter)
        if (!hasColorHighlight) return
      }

      result.push({ segment, originalIndex: index })
    })

    return result
  }, [transcription.transcript, timeRange, isTimeFilterActive, selectedSpeakers, confidenceFilter, annotationFilter, highlightColorFilter, annotations, getSegmentAnnotationsByIndex])

  // Calcular número total de páginas
  const totalPages = Math.ceil(filteredTranscript.length / ITEMS_PER_PAGE)

  // Itens da página atual (modo inline)
  const currentPageItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredTranscript.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [filteredTranscript, currentPage])

  // Ajustar página quando filtros mudam
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }
  }, [totalPages, currentPage])

  // Limpar todos os filtros
  const handleClearAllFilters = useCallback(() => {
    setTimeRange([0, totalDuration])
    setIsTimeFilterActive(false)
    setAnnotationFilter('all')
    setSelectedSpeakers([])
    setConfidenceFilter('all')
    setHighlightColorFilter('all')
    setSearchQuery('')
    setCurrentPage(1)
  }, [totalDuration])

  const handleNextResult = useCallback(() => {
    if (resultSegmentIndices.length > 0) {
      setCurrentResultIndex((prev) => (prev + 1) % resultSegmentIndices.length)
    }
  }, [resultSegmentIndices.length])

  const handlePrevResult = useCallback(() => {
    if (resultSegmentIndices.length > 0) {
      setCurrentResultIndex((prev) => (prev - 1 + resultSegmentIndices.length) % resultSegmentIndices.length)
    }
  }, [resultSegmentIndices.length])

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    setCurrentResultIndex(0)
  }, [])

  // Abrir menu de contexto
  const handleContextMenu = useCallback((event: React.MouseEvent, segmentIndex: number) => {
    event.preventDefault()
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, segmentIndex })
  }, [])

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [])

  // Adicionar/editar anotação
  const handleOpenAnnotationDialog = useCallback((segmentIndex: number, editingId?: string) => {
    if (editingId && annotations) {
      const existing = annotations.items.find(a => a.id === editingId)
      if (existing) {
        setAnnotationText(existing.text)
        setAnnotationColor(existing.color)
        setAnnotationCategory(existing.category)
      }
    } else {
      setAnnotationText('')
      setAnnotationColor('yellow')
      setAnnotationCategory('note')
    }
    setAnnotationDialog({ open: true, segmentIndex, editingId })
    handleCloseContextMenu()
    onPauseForAction?.()
  }, [annotations, handleCloseContextMenu, onPauseForAction])

  const handleCloseAnnotationDialog = useCallback(() => {
    setAnnotationDialog(null)
    setAnnotationText('')
    setAnnotationColor('yellow')
    setAnnotationCategory('note')
    onResumeAfterAction?.()
  }, [onResumeAfterAction])

  const handleSaveAnnotation = useCallback(() => {
    if (!annotationDialog || !onAnnotationsChange || !annotationText.trim()) return

    const segment = transcription.transcript[annotationDialog.segmentIndex]
    const now = Date.now()

    const currentAnnotations = annotationsRef.current || { items: [], highlights: [], bookmarks: [] }

    if (annotationDialog.editingId) {
      // Editando existente
      const updatedItems = currentAnnotations.items.map(a =>
        a.id === annotationDialog.editingId
          ? { ...a, text: annotationText, color: annotationColor, category: annotationCategory, updatedAt: now }
          : a
      )
      onAnnotationsChange({ ...currentAnnotations, items: updatedItems })
    } else {
      // Nova anotação
      const newAnnotation: Annotation = {
        id: `ann_${now}_${Math.random().toString(36).substr(2, 9)}`,
        segmentIndex: annotationDialog.segmentIndex,
        startTime: segment.start_time,
        text: annotationText,
        segmentText: segment.text.substring(0, 100),
        color: annotationColor,
        category: annotationCategory,
        createdAt: now,
        updatedAt: now
      }
      onAnnotationsChange({ ...currentAnnotations, items: [...currentAnnotations.items, newAnnotation] })
    }

    handleCloseAnnotationDialog()
  }, [annotationDialog, onAnnotationsChange, annotationText, annotationColor, annotationCategory, transcription.transcript, handleCloseAnnotationDialog])

  // Toggle destaque
  const handleToggleHighlight = useCallback((segmentIndex: number, color: AnnotationColor = 'yellow') => {
    if (!onAnnotationsChange) return

    const segment = transcription.transcript[segmentIndex]
    const currentAnnotations = annotationsRef.current || { items: [], highlights: [], bookmarks: [] }
    const existingHighlight = currentAnnotations.highlights.find(h => h.segmentIndex === segmentIndex)

    if (existingHighlight) {
      onAnnotationsChange({
        ...currentAnnotations,
        highlights: currentAnnotations.highlights.filter(h => h.id !== existingHighlight.id)
      })
    } else {
      const newHighlight: SegmentHighlight = {
        id: `high_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        segmentIndex,
        startTime: segment.start_time,
        color,
        createdAt: Date.now()
      }
      onAnnotationsChange({ ...currentAnnotations, highlights: [...currentAnnotations.highlights, newHighlight] })
    }
    handleCloseContextMenu()
  }, [onAnnotationsChange, transcription.transcript, handleCloseContextMenu])

  // Toggle favorito
  const handleToggleBookmark = useCallback((segmentIndex: number) => {
    if (!onAnnotationsChange) return

    const segment = transcription.transcript[segmentIndex]
    const currentAnnotations = annotationsRef.current || { items: [], highlights: [], bookmarks: [] }
    const existingBookmark = currentAnnotations.bookmarks.find(b => b.segmentIndex === segmentIndex)

    if (existingBookmark) {
      onAnnotationsChange({
        ...currentAnnotations,
        bookmarks: currentAnnotations.bookmarks.filter(b => b.id !== existingBookmark.id)
      })
    } else {
      const newBookmark: Bookmark = {
        id: `bm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        segmentIndex,
        startTime: segment.start_time,
        title: `Segmento ${segmentIndex + 1}`,
        description: segment.text.substring(0, 80),
        createdAt: Date.now()
      }
      onAnnotationsChange({ ...currentAnnotations, bookmarks: [...currentAnnotations.bookmarks, newBookmark] })
    }
    handleCloseContextMenu()
  }, [onAnnotationsChange, transcription.transcript, handleCloseContextMenu])

  // Deletar anotação
  const handleDeleteAnnotation = useCallback((annotationId: string) => {
    if (!onAnnotationsChange || !annotationsRef.current) return
    const curr = annotationsRef.current
    onAnnotationsChange({
      ...curr,
      items: curr.items.filter(a => a.id !== annotationId)
    })
  }, [onAnnotationsChange])

  // --- Edição de segmentos (salvamento direto por segmento) ---
  const handleSegmentSave = useCallback(async (segmentIndex: number, text: string, speaker: string): Promise<boolean> => {
    if (!fileId) return false

    try {
      await saveSegmentEdits(fileId, [{ segmentIndex, text, speaker }])

      // Update transcription locally to reflect changes immediately
      if (onTranscriptionChange) {
        const updatedTranscript = transcription.transcript.map((seg, idx) => {
          if (idx === segmentIndex) {
            return { ...seg, text, speaker: speaker || seg.speaker }
          }
          return seg
        })
        onTranscriptionChange({
          ...transcription,
          transcript: updatedTranscript,
          full_text: updatedTranscript.map(s => `${s.speaker ? s.speaker + ': ' : ''}${s.text}`).join('\n')
        })
      }

      setCopySnackbar({
        open: true,
        message: 'Segmento atualizado com sucesso.',
        severity: 'success'
      })
      return true
    } catch (error) {
      console.error('Failed to save segment edit:', error)
      setCopySnackbar({
        open: true,
        message: 'Erro ao salvar edição. Tente novamente.',
        severity: 'error'
      })
      return false
    }
  }, [fileId, transcription, onTranscriptionChange])

  const getSpeakerColor = useCallback((speaker: string): string => {
    const colors = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f', '#0288d1']
    const index = transcription.participants.indexOf(speaker)
    return colors[index >= 0 ? index % colors.length : Math.abs(speaker.charCodeAt(0)) % colors.length]
  }, [transcription.participants])

  const fallbackCopyToClipboard = useCallback((text: string): boolean => {
    try {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.setAttribute('readonly', '')
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      textArea.style.left = '-9999px'

      const container = document.getElementById('monkee-file-transcript-client') || document.body
      container.appendChild(textArea)
      textArea.select()
      textArea.setSelectionRange(0, text.length)

      const copied = document.execCommand('copy')
      container.removeChild(textArea)
      return copied
    } catch {
      return false
    }
  }, [])

  const buildCopyTranscriptText = useCallback(() => {
    return filteredTranscript
      .map(({ segment }) => {
        const timestamp = `[${formatTimestamp(segment.start_time)}]`
        const speaker = segment.speaker ? `${getSpeakerName(segment.speaker)}: ` : ''
        return `${timestamp} ${speaker}${segment.text}`.trim()
      })
      .join('\n\n')
  }, [filteredTranscript, getSpeakerName])

  const handleCopyTranscript = useCallback(async () => {
    if (filteredTranscript.length === 0) return

    const text = buildCopyTranscriptText()
    if (!text.trim()) return

    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text)
      } else {
        const copied = fallbackCopyToClipboard(text)
        if (!copied) throw new Error('Clipboard fallback failed')
      }

      setCopySuccess(true)
      setCopySnackbar({
        open: true,
        message: `${filteredTranscript.length} segmento(s) copiado(s) para a área de transferência.`,
        severity: 'success'
      })
    } catch {
      const copied = fallbackCopyToClipboard(text)
      if (copied) {
        setCopySuccess(true)
        setCopySnackbar({
          open: true,
          message: `${filteredTranscript.length} segmento(s) copiado(s) para a área de transferência.`,
          severity: 'success'
        })
        return
      }

      setCopySnackbar({
        open: true,
        message: 'Não foi possível copiar a transcrição. Tente novamente.',
        severity: 'error'
      })
    }
  }, [filteredTranscript, buildCopyTranscriptText, fallbackCopyToClipboard])

  useEffect(() => {
    if (!copySuccess) return
    const timeoutId = window.setTimeout(() => setCopySuccess(false), 1500)
    return () => window.clearTimeout(timeoutId)
  }, [copySuccess])

  // Renderizar texto com highlight de busca ou termos jurídicos
  const renderHighlightedText = useCallback((text: string, segmentIndex: number): React.ReactNode => {
    // Primeiro, aplica highlight de busca
    if (searchQuery.trim()) {
      const parts: React.ReactNode[] = []
      const lowerText = text.toLowerCase()
      const lowerQuery = searchQuery.toLowerCase()
      let lastEnd = 0

      let searchIndex = 0
      while (true) {
        const matchStart = lowerText.indexOf(lowerQuery, searchIndex)
        if (matchStart === -1) break

        if (matchStart > lastEnd) {
          parts.push(<span key={`text-${lastEnd}`}>{text.substring(lastEnd, matchStart)}</span>)
        }

        const isCurrentResult = resultSegmentIndices[currentResultIndex] === segmentIndex
        parts.push(
          <mark
            key={`match-${matchStart}`}
            style={{
              backgroundColor: isCurrentResult ? '#ffeb3b' : '#fff59d',
              padding: '0 2px',
              borderRadius: 2
            }}
          >
            {text.substring(matchStart, matchStart + searchQuery.length)}
          </mark>
        )

        lastEnd = matchStart + searchQuery.length
        searchIndex = matchStart + 1
      }

      if (lastEnd < text.length) {
        parts.push(<span key={`text-${lastEnd}`}>{text.substring(lastEnd)}</span>)
      }

      return <>{parts}</>
    }

    // Se não há busca, aplica highlight de termos jurídicos
    if (highlightTerms && highlightTermsVisible) {
      const parts = highlightLegalTerms(text, customTerms)
      return (
        <>
          {parts.map((part, i) =>
            part.isHighlight && part.term ? (
              <Tooltip
                key={i}
                title={
                  <Box>
                    <Typography variant="subtitle2">{part.term.term}</Typography>
                    <Typography variant="caption">{part.term.description}</Typography>
                  </Box>
                }
                arrow
              >
                <span
                  style={{
                    backgroundColor: CATEGORY_COLORS[part.term.category]?.bg || '#f5f5f5',
                    color: CATEGORY_COLORS[part.term.category]?.text || '#333',
                    padding: '0 3px',
                    borderRadius: 3,
                    cursor: 'help'
                  }}
                >
                  {part.text}
                </span>
              </Tooltip>
            ) : (
              <span key={i}>{part.text}</span>
            )
          )}
        </>
      )
    }

    return text
  }, [searchQuery, resultSegmentIndices, currentResultIndex, highlightTerms, highlightTermsVisible, customTerms])

  // Scroll para segmento específico quando solicitado externamente
  useEffect(() => {
    if (scrollToSegmentIndex !== null && scrollToSegmentIndex !== undefined && scrollToSegmentIndex >= 0) {
      // Se o filtro de tempo está ativo, desabilitar para garantir que o segmento está visível
      if (isTimeFilterActive) {
        setIsTimeFilterActive(false)
        setTimeRange([0, totalDuration])
      }

      // Encontrar o índice filtrado
      const filteredIndex = filteredTranscript.findIndex(item => item.originalIndex === scrollToSegmentIndex)

      if (filteredIndex >= 0) {
        // Calcular a página onde o segmento está
        const targetPage = Math.ceil((filteredIndex + 1) / ITEMS_PER_PAGE)
        setCurrentPage(targetPage)

        // Destacar o segmento temporariamente
        setHighlightedSegment(scrollToSegmentIndex)
        setTimeout(() => setHighlightedSegment(null), 2000)
      }
    }
  }, [scrollToSegmentIndex, isTimeFilterActive, totalDuration, filteredTranscript])

  // Auto-scroll para segmento ativo durante reprodução de mídia
  const currentPageRef = useRef(currentPage)
  currentPageRef.current = currentPage

  useEffect(() => {
    if (activeSegmentIndex == null || activeSegmentIndex < 0) return

    // Encontrar o índice no array filtrado
    const filteredIndex = filteredTranscript.findIndex(item => item.originalIndex === activeSegmentIndex)
    if (filteredIndex < 0) return

    // Calcular a página alvo e trocar se necessário
    const targetPage = Math.ceil((filteredIndex + 1) / ITEMS_PER_PAGE)
    const needsPageChange = targetPage !== currentPageRef.current

    if (needsPageChange) {
      setCurrentPage(targetPage)
    }

    // Scroll para o elemento do segmento ativo (com delay se mudou de página)
    const delay = needsPageChange ? 120 : 0
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-segment-index="${activeSegmentIndex}"]`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, delay)

    return () => clearTimeout(timer)
  }, [activeSegmentIndex, filteredTranscript])

  // Navegar para resultado de busca no modo inline
  useEffect(() => {
    if (resultSegmentIndices.length > 0 && currentResultIndex < resultSegmentIndices.length) {
      const targetOriginalIndex = resultSegmentIndices[currentResultIndex]
      const filteredIndex = filteredTranscript.findIndex(item => item.originalIndex === targetOriginalIndex)
      if (filteredIndex >= 0) {
        const targetPage = Math.ceil((filteredIndex + 1) / ITEMS_PER_PAGE)
        setCurrentPage(targetPage)
      }
    }
  }, [currentResultIndex, resultSegmentIndices, filteredTranscript])

  // Abrir fullscreen e ir para segmento
  const handleOpenFullscreen = useCallback(() => {
    setIsFullscreenOpen(true)
  }, [])

  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreenOpen(false)
  }, [])

  // Renderizar um item de segmento (usado em ambos os modos)
  const renderSegmentItem = useCallback((item: { segment: TranscriptSegment; originalIndex: number }, index: number) => {
    const { segment, originalIndex } = item
    const isSearchMatch = resultSegmentIndices.includes(originalIndex)
    const isCurrentMatch = resultSegmentIndices[currentResultIndex] === originalIndex
    const isHighlighted = highlightedSegment === originalIndex
    const isActive = activeSegmentIndex === originalIndex
    const segAnnotations = getSegmentAnnotationsByIndex(originalIndex)

    return (
      <TranscriptSegmentItem
        key={originalIndex}
        segment={segment}
        originalIndex={originalIndex}
        isSearchMatch={isSearchMatch}
        isCurrentMatch={isCurrentMatch}
        isHighlighted={isHighlighted}
        isActive={isActive}
        annotations={segAnnotations}
        speakerColor={segment.speaker ? getSpeakerColor(segment.speaker) : '#e0e0e0'}
        speakerName={segment.speaker ? getSpeakerName(segment.speaker) : ''}
        canEdit={!!onAnnotationsChange}
        renderedText={renderHighlightedText(segment.text, originalIndex)}
        onSegmentSave={fileId ? handleSegmentSave : undefined}
        onContextMenu={handleContextMenu}
        onOpenAnnotationDialog={handleOpenAnnotationDialog}
        onToggleHighlight={handleToggleHighlight}
        onToggleBookmark={handleToggleBookmark}
        onDeleteAnnotation={handleDeleteAnnotation}
        onPauseForAction={onPauseForAction}
        onResumeAfterAction={onResumeAfterAction}
        onPlaySegment={onPlaySegment}
        isSegmentPlaying={playingSegmentIndex === originalIndex}
        onStopSegmentPlayback={onStopSegmentPlayback}
        mediaAvailable={mediaAvailable}
      />
    )
  }, [
    resultSegmentIndices,
    currentResultIndex,
    highlightedSegment,
    activeSegmentIndex,
    getSegmentAnnotationsByIndex,
    getSpeakerColor,
    getSpeakerName,
    onAnnotationsChange,
    renderHighlightedText,
    fileId,
    handleSegmentSave,
    handleContextMenu,
    handleOpenAnnotationDialog,
    handleToggleHighlight,
    handleToggleBookmark,
    handleDeleteAnnotation,
    onPauseForAction,
    onResumeAfterAction,
    onPlaySegment,
    playingSegmentIndex,
    onStopSegmentPlayback,
    mediaAvailable
  ])

  // Componente de barra de busca e filtros (reutilizado em ambos os modos)
  const SearchAndFiltersBar = useCallback(({ compact = false }: { compact?: boolean }) => (
    <Paper
      elevation={0}
      sx={{
        mb: compact ? 1 : { xs: 1, sm: 2 },
        bgcolor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: compact ? 1 : { xs: 1.5, sm: 2 },
        overflow: 'hidden',
        flexShrink: 0,
        '#monkee-file-transcript-client &': { overflow: 'hidden !important' },
      }}
    >
      {/* Linha principal: Busca + Botão de Filtros */}
      <Box sx={{ p: compact ? 1 : { xs: 1, sm: 2 }, display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
        <TextField
          placeholder="Buscar na transcrição..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setCurrentResultIndex(0)
          }}
          size="small"
          sx={{ flex: 1, bgcolor: '#fff' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'action.active' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        {/* Navegação de resultados de busca */}
        {searchResults.length > 0 && (
          <>
            <Chip
              label={`${currentResultIndex + 1}/${resultSegmentIndices.length}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <IconButton size="small" onClick={handlePrevResult}>
              <KeyboardArrowUpIcon />
            </IconButton>
            <IconButton size="small" onClick={handleNextResult}>
              <KeyboardArrowDownIcon />
            </IconButton>
          </>
        )}

        {searchQuery && searchResults.length === 0 && (
          <Chip label="Sem resultados" size="small" color="error" variant="outlined" />
        )}

        {/* Botão de filtros avançados */}
        <Tooltip title="Filtros avançados">
          <IconButton
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            sx={{
              bgcolor: filtersExpanded || activeFilterCount > 0 ? '#e3f2fd' : 'transparent',
              color: activeFilterCount > 0 ? '#1976d2' : 'inherit'
            }}
          >
            <Badge badgeContent={activeFilterCount} color="primary" max={9}>
              <TuneIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title={copySuccess ? 'Copiado!' : 'Copiar transcrição'}>
          <span>
            <IconButton
              onClick={handleCopyTranscript}
              disabled={filteredTranscript.length === 0}
              sx={{
                bgcolor: copySuccess ? '#e8f5e9' : 'transparent',
                color: copySuccess ? '#2e7d32' : 'inherit'
              }}
            >
              {copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
            </IconButton>
          </span>
        </Tooltip>

        {/* Botão de player de mídia */}
        {onTogglePlayer && mediaAvailable && (
          <Tooltip title={playerEnabled ? 'Desativar player' : 'Ativar player de áudio'}>
            <IconButton
              onClick={onTogglePlayer}
              sx={{
                bgcolor: playerEnabled ? '#e8f5e9' : '#f3e5f5',
                color: playerEnabled ? '#2e7d32' : '#7b1fa2',
                '&:hover': {
                  bgcolor: playerEnabled ? '#c8e6c9' : '#e1bee7'
                }
              }}
            >
              {playerEnabled ? <GraphicEqIcon /> : <HeadphonesIcon />}
            </IconButton>
          </Tooltip>
        )}

        {/* Botão de expandir (apenas no modo inline) */}
        {!compact && (
          <Tooltip title="Abrir em tela cheia">
            <IconButton
              onClick={handleOpenFullscreen}
              sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
            >
              <FullscreenIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Chips de filtros ativos (resumo) */}
      {hasActiveFilters && !filtersExpanded && (
        <Box sx={{ px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 1.5 }, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {isTimeFilterActive && (
            <Chip
              size="small"
              label={`⏱️ ${formatSeconds(timeRange[0])} - ${formatSeconds(timeRange[1])}`}
              onDelete={() => { setTimeRange([0, totalDuration]); setIsTimeFilterActive(false) }}
              sx={{ height: 24 }}
            />
          )}
          {annotationFilter !== 'all' && (
            <Chip
              size="small"
              icon={annotationFilter === 'bookmarks' ? <BookmarkIcon sx={{ fontSize: 16 }} /> :
                    annotationFilter === 'notes' ? <EditNoteIcon sx={{ fontSize: 16 }} /> :
                    annotationFilter === 'highlights' ? <HighlightIcon sx={{ fontSize: 16 }} /> :
                    <FilterListIcon sx={{ fontSize: 16 }} />}
              label={annotationFilter === 'bookmarks' ? 'Favoritos' :
                     annotationFilter === 'notes' ? 'Com notas' :
                     annotationFilter === 'highlights' ? 'Destacados' : 'Com anotação'}
              onDelete={() => setAnnotationFilter('all')}
              sx={{ height: 24 }}
            />
          )}
          {selectedSpeakers.length > 0 && (
            <Chip
              size="small"
              icon={<PersonIcon sx={{ fontSize: 16 }} />}
              label={`${selectedSpeakers.length} participante(s)`}
              onDelete={() => setSelectedSpeakers([])}
              sx={{ height: 24 }}
            />
          )}
          {confidenceFilter !== 'all' && (
            <Chip
              size="small"
              icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
              label={confidenceFilter === 'low' ? 'Baixa confiança' :
                     confidenceFilter === 'medium' ? 'Média confiança' : 'Alta confiança'}
              onDelete={() => setConfidenceFilter('all')}
              sx={{ height: 24 }}
            />
          )}
          {highlightColorFilter !== 'all' && (
            <Chip
              size="small"
              sx={{
                height: 24,
                bgcolor: ANNOTATION_COLORS[highlightColorFilter].bg,
                borderColor: ANNOTATION_COLORS[highlightColorFilter].border
              }}
              label={`Destaque ${ANNOTATION_COLORS[highlightColorFilter].label}`}
              onDelete={() => setHighlightColorFilter('all')}
            />
          )}
          <Chip
            size="small"
            label="Limpar tudo"
            variant="outlined"
            onClick={handleClearAllFilters}
            sx={{ height: 24 }}
          />
        </Box>
      )}

      {/* Painel de filtros expandido */}
      <Collapse in={filtersExpanded}>
        <Divider />
        <Box sx={{ p: compact ? 1.5 : { xs: 1.5, sm: 2 }, bgcolor: '#fff' }}>
          {/* Filtros rápidos por tipo de anotação */}
          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              {isMobile ? 'Anotações' : 'Filtrar por Anotações'}
            </Typography>
            <ToggleButtonGroup
              value={annotationFilter}
              exclusive
              onChange={(_, value) => value && setAnnotationFilter(value)}
              size="small"
              sx={{ flexWrap: 'wrap', gap: { xs: 0.5, sm: 0 } }}
            >
              <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, py: { xs: 0.5, sm: 1 } }}>
                Todos
              </ToggleButton>
              <ToggleButton value="bookmarks" sx={{ textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, py: { xs: 0.5, sm: 1 } }}>
                <BookmarkIcon sx={{ fontSize: { xs: 14, sm: 18 }, mr: 0.5 }} /> {isMobile ? '' : 'Favoritos'}
                {annotations && annotations.bookmarks.length > 0 && (
                  <Chip size="small" label={annotations.bookmarks.length} sx={{ ml: 0.5, height: { xs: 16, sm: 18 }, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                )}
              </ToggleButton>
              <ToggleButton value="notes" sx={{ textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, py: { xs: 0.5, sm: 1 } }}>
                <EditNoteIcon sx={{ fontSize: { xs: 14, sm: 18 }, mr: 0.5 }} /> {isMobile ? '' : 'Com notas'}
                {annotations && annotations.items.length > 0 && (
                  <Chip size="small" label={annotations.items.length} sx={{ ml: 0.5, height: { xs: 16, sm: 18 }, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                )}
              </ToggleButton>
              <ToggleButton value="highlights" sx={{ textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, py: { xs: 0.5, sm: 1 } }}>
                <HighlightIcon sx={{ fontSize: { xs: 14, sm: 18 }, mr: 0.5 }} /> {isMobile ? '' : 'Destaques'}
                {annotations && annotations.highlights.length > 0 && (
                  <Chip size="small" label={annotations.highlights.length} sx={{ ml: 0.5, height: { xs: 16, sm: 18 }, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                )}
              </ToggleButton>
              <ToggleButton value="any-annotation" sx={{ textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, py: { xs: 0.5, sm: 1 } }}>
                {isMobile ? 'Qualquer' : 'Qualquer anotação'}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Filtro por cor de destaque */}
          {annotationFilter === 'highlights' && annotations && annotations.highlights.length > 0 && (
            <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Cor do destaque
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label="Todas"
                  variant={highlightColorFilter === 'all' ? 'filled' : 'outlined'}
                  onClick={() => setHighlightColorFilter('all')}
                />
                {Object.entries(ANNOTATION_COLORS).map(([color, { bg, border, label }]) => (
                  <Chip
                    key={color}
                    size="small"
                    label={label}
                    variant={highlightColorFilter === color ? 'filled' : 'outlined'}
                    onClick={() => setHighlightColorFilter(color as AnnotationColor)}
                    sx={{
                      bgcolor: highlightColorFilter === color ? bg : 'transparent',
                      borderColor: border,
                      '&:hover': { bgcolor: bg }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Filtro por participante */}
          {transcription.participants.length > 1 && (
            <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                {isMobile ? 'Participante' : 'Filtrar por Participante'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {transcription.participants.map((speaker) => (
                  <Chip
                    key={speaker}
                    size="small"
                    icon={<PersonIcon sx={{ fontSize: 16 }} />}
                    label={getSpeakerName(speaker)}
                    variant={selectedSpeakers.includes(speaker) ? 'filled' : 'outlined'}
                    onClick={() => {
                      if (selectedSpeakers.includes(speaker)) {
                        setSelectedSpeakers(selectedSpeakers.filter(s => s !== speaker))
                      } else {
                        setSelectedSpeakers([...selectedSpeakers, speaker])
                      }
                    }}
                    sx={{
                      borderColor: getSpeakerColor(speaker),
                      bgcolor: selectedSpeakers.includes(speaker) ? getSpeakerColor(speaker) : 'transparent',
                      color: selectedSpeakers.includes(speaker) ? '#fff' : 'inherit',
                      '&:hover': { opacity: 0.8 }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Filtro por confiança */}
          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              {isMobile ? 'Confiança' : 'Filtrar por Confiança da Transcrição'}
            </Typography>
            <ToggleButtonGroup
              value={confidenceFilter}
              exclusive
              onChange={(_, value) => value && setConfidenceFilter(value)}
              size="small"
              sx={{ flexWrap: 'wrap', gap: { xs: 0.5, sm: 0 } }}
            >
              <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, py: { xs: 0.5, sm: 1 } }}>
                Todos
              </ToggleButton>
              <ToggleButton value="low" sx={{ textTransform: 'none', color: '#d32f2f', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, py: { xs: 0.5, sm: 1 } }}>
                ⚠️ {isMobile ? '<70%' : 'Baixa (<70%)'}
              </ToggleButton>
              <ToggleButton value="medium" sx={{ textTransform: 'none', color: '#ed6c02', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, py: { xs: 0.5, sm: 1 } }}>
                {isMobile ? '70-90%' : 'Média (70-90%)'}
              </ToggleButton>
              <ToggleButton value="high" sx={{ textTransform: 'none', color: '#2e7d32', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, py: { xs: 0.5, sm: 1 } }}>
                ✓ {isMobile ? '>90%' : 'Alta (>90%)'}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Slider de tempo */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, mb: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
              <AccessTimeIcon sx={{ fontSize: { xs: 14, sm: 18 }, color: isTimeFilterActive ? '#1976d2' : '#666' }} />
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, flex: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                {isMobile ? 'Momento' : 'Filtrar por Momento'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                {formatSeconds(timeRange[0])} - {formatSeconds(timeRange[1])}
              </Typography>
              {isTimeFilterActive && (
                <Chip
                  label="Limpar"
                  size="small"
                  onClick={() => {
                    setTimeRange([0, totalDuration])
                    setIsTimeFilterActive(false)
                  }}
                  sx={{ height: 22 }}
                />
              )}
            </Box>
            <Slider
              value={timeRange}
              onChange={(_, newValue) => {
                setTimeRange(newValue as [number, number])
                setIsTimeFilterActive(true)
              }}
              valueLabelDisplay="auto"
              valueLabelFormat={formatSeconds}
              min={0}
              max={totalDuration}
              step={1}
              sx={{
                color: isTimeFilterActive ? '#1976d2' : '#bdbdbd',
                '& .MuiSlider-thumb': { width: 16, height: 16 },
                '& .MuiSlider-valueLabel': { fontFamily: 'monospace', fontSize: 11 }
              }}
            />
          </Box>

          {/* Botão limpar todos */}
          {hasActiveFilters && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearAllFilters}
              >
                Limpar todos os filtros
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Status de resultados */}
      <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.5, sm: 1 }, bgcolor: hasActiveFilters ? '#e3f2fd' : '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="caption" sx={{ color: hasActiveFilters ? '#1976d2' : '#666', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
          {hasActiveFilters
            ? (isMobile
              ? `${filteredTranscript.length}/${transcription.transcript.length} segmentos`
              : `Exibindo ${filteredTranscript.length} de ${transcription.transcript.length} segmentos`)
            : (isMobile
              ? `${transcription.transcript.length} seg. • ${formatSeconds(totalDuration)}`
              : `${transcription.transcript.length} segmentos • ${formatSeconds(totalDuration)} de duração`)
          }
        </Typography>
      </Box>
    </Paper>
  ), [
    searchQuery, searchResults, resultSegmentIndices, currentResultIndex,
    filtersExpanded, activeFilterCount, hasActiveFilters, isTimeFilterActive,
    timeRange, totalDuration, annotationFilter, selectedSpeakers, confidenceFilter,
    highlightColorFilter, annotations, transcription, filteredTranscript, isMobile,
    handleClearSearch, handlePrevResult, handleNextResult, handleClearAllFilters,
    handleOpenFullscreen, formatSeconds, getSpeakerName, getSpeakerColor,
    copySuccess, handleCopyTranscript
  ])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Editor de Speakers */}
      {onSpeakerMappingChange && (
        <SpeakerEditor
          transcription={transcription}
          speakerMapping={speakerMapping}
          onSpeakerMappingChange={onSpeakerMappingChange}
        />
      )}

      {/* Barra de busca e filtros - MODO INLINE */}
      <SearchAndFiltersBar />

      {/* Barra unificada de legenda — Termos + Anotações */}
      <ToolsLegendBar
        terms={customTerms}
        termsVisible={highlightTermsVisible}
        onToggleTermsVisibility={() => setHighlightTermsVisible(v => !v)}
        onOpenTermsPanel={() => setActiveTool(prev => prev === 'terms' ? null : 'terms')}
        annotations={annotations || { items: [], highlights: [], bookmarks: [] }}
        annotationsVisible={annotationsVisible}
        onToggleAnnotationsVisibility={() => setAnnotationsVisible(v => !v)}
        onOpenAnnotationsPanel={() => setActiveTool(prev => prev === 'annotations' ? null : 'annotations')}
      />

      {/* Área de segmentos + painel lateral de ferramentas */}
      <Box sx={{ display: 'flex' }}>
        {/* Coluna principal de segmentos */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Lista de segmentos - MODO INLINE (Paginado) */}
          {filteredTranscript.length === 0 ? (
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <FilterListIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: '#bdbdbd', mb: 1 }} />
              <Typography variant="body1" sx={{ color: '#666', mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Nenhum segmento encontrado
              </Typography>
          <Typography variant="body2" sx={{ color: '#999', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {isMobile ? 'Ajuste os filtros' : 'Tente ajustar os filtros ou limpar a busca'}
          </Typography>
          <Button variant="outlined" size="small" onClick={handleClearAllFilters} sx={{ minHeight: { xs: 36, sm: 32 } }}>
            Limpar filtros
          </Button>
        </Paper>
      ) : (
        <>
          {/* Lista paginada */}
          <Box sx={{ bgcolor: '#fff' }}>
            {currentPageItems.map((item, index) => renderSegmentItem(item, index))}
          </Box>

          {/* Paginação */}
          {totalPages > 1 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              mt: 2,
              py: 1.5,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              '#monkee-file-transcript-client &': {
                borderRadius: '8px !important',
              },
            }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
              <Tooltip title="Abrir em tela cheia para visualização completa">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FullscreenIcon />}
                  onClick={handleOpenFullscreen}
                  sx={{ textTransform: 'none' }}
                >
                  Tela cheia
                </Button>
              </Tooltip>
            </Box>
          )}
        </>
      )}
        </Box>

        {/* Painel lateral de ferramentas */}
        <ToolsSidePanel
          tools={toolsConfig}
          activeTool={activeTool}
          onToolChange={setActiveTool}
        >
          {activeTool === 'terms' && (
            <CustomTermsManager
              ref={termsManagerRef}
              fileId={fileId || ''}
              compact
            />
          )}
          {activeTool === 'annotations' && (
            <AnnotationsPanel
              ref={annotationsPanelRef}
              fileId={fileId || ''}
              transcription={transcription}
              onNavigateToSegment={onTimestampClick ? (idx) => {
                const seg = transcription?.transcript?.[idx]
                if (seg) onTimestampClick(parseFloat(seg.start_time))
              } : undefined}
              annotations={annotations}
              onAnnotationsChange={onAnnotationsChange}
              compact
            />
          )}
          {activeTool === 'chat' && (
            <AIChatPanel
              fileId={fileId || ''}
              onTimestampClick={onTimestampClick}
            />
          )}
        </ToolsSidePanel>
      </Box>

      {/* MODAL FULLSCREEN */}
      <Dialog
        fullScreen
        open={isFullscreenOpen}
        onClose={handleCloseFullscreen}
        TransitionComponent={Fade}
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: '#f8f9fa'
          }
        }}
      >
        {/* AppBar */}
        <AppBar sx={{ position: 'relative', bgcolor: '#1976d2' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseFullscreen}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Transcrição - Visualização Completa
            </Typography>
            <Chip
              label={`${filteredTranscript.length} segmentos`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}
            />
          </Toolbar>
        </AppBar>

        {/* Conteúdo do Fullscreen */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
          overflow: 'hidden'
        }}>
          {/* Barra de busca compacta */}
          <Box sx={{ p: 1, flexShrink: 0 }}>
            <SearchAndFiltersBar compact />
          </Box>

          {/* Lista virtualizada com react-virtuoso */}
          {filteredTranscript.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <FilterListIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
                  Nenhum segmento encontrado
                </Typography>
                <Button variant="outlined" size="small" onClick={handleClearAllFilters}>
                  Limpar filtros
                </Button>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ flex: 1, px: 1, pb: 1 }}>
              <Virtuoso
                ref={virtuosoRef}
                data={filteredTranscript}
                itemContent={(index, item) => (
                  <Box sx={{ py: 0.5 }}>
                    {renderSegmentItem(item, index)}
                  </Box>
                )}
                style={{ height: '100%' }}
                overscan={200}
              />
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Menu de contexto */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => contextMenu && handleOpenAnnotationDialog(contextMenu.segmentIndex)}>
          <ListItemIcon><NoteAddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Adicionar nota</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => contextMenu && handleToggleHighlight(contextMenu.segmentIndex, 'yellow')}>
          <ListItemIcon><HighlightIcon fontSize="small" sx={{ color: ANNOTATION_COLORS.yellow.border }} /></ListItemIcon>
          <ListItemText>Destacar amarelo</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleToggleHighlight(contextMenu.segmentIndex, 'green')}>
          <ListItemIcon><HighlightIcon fontSize="small" sx={{ color: ANNOTATION_COLORS.green.border }} /></ListItemIcon>
          <ListItemText>Destacar verde</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleToggleHighlight(contextMenu.segmentIndex, 'blue')}>
          <ListItemIcon><HighlightIcon fontSize="small" sx={{ color: ANNOTATION_COLORS.blue.border }} /></ListItemIcon>
          <ListItemText>Destacar azul</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => contextMenu && handleToggleHighlight(contextMenu.segmentIndex, 'red')}>
          <ListItemIcon><HighlightIcon fontSize="small" sx={{ color: ANNOTATION_COLORS.red.border }} /></ListItemIcon>
          <ListItemText>Destacar vermelho</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => contextMenu && handleToggleBookmark(contextMenu.segmentIndex)}>
          <ListItemIcon><BookmarkBorderIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Favoritar</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          const selectedText = window.getSelection()?.toString()?.trim() || ''
          handleCloseContextMenu()
          setActiveTool('terms')
          setTimeout(() => termsManagerRef.current?.openAdd(selectedText), 150)
        }}>
          <ListItemIcon><LabelIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Criar termo</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleCloseContextMenu()
          setActiveTool('annotations')
        }}>
          <ListItemIcon><EditNoteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Gerenciar anotações</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog para adicionar/editar anotação */}
      <Dialog open={annotationDialog?.open || false} onClose={handleCloseAnnotationDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {annotationDialog?.editingId ? 'Editar Nota' : 'Nova Nota'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nota"
              multiline
              rows={4}
              value={annotationText}
              onChange={(e) => setAnnotationText(e.target.value)}
              fullWidth
              autoFocus
              placeholder="Digite sua anotação..."
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={annotationCategory}
                  label="Categoria"
                  onChange={(e) => setAnnotationCategory(e.target.value as AnnotationCategory)}
                >
                  {Object.entries(ANNOTATION_CATEGORIES).map(([key, { icon, label }]) => (
                    <MenuItem key={key} value={key}>
                      {icon} {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 1 }}>Cor:</Typography>
                {Object.entries(ANNOTATION_COLORS).map(([key, { bg, border }]) => (
                  <IconButton
                    key={key}
                    size="small"
                    onClick={() => setAnnotationColor(key as AnnotationColor)}
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: bg,
                      border: annotationColor === key ? `2px solid ${border}` : '1px solid #ccc',
                      '&:hover': { bgcolor: bg }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAnnotationDialog}>Cancelar</Button>
          <Button onClick={handleSaveAnnotation} variant="contained" disabled={!annotationText.trim()}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={3000}
        onClose={() => setCopySnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCopySnackbar(prev => ({ ...prev, open: false }))}
          severity={copySnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {copySnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
